// ----- Phase 5.25.1 — XML tab content ----- //
//
// State machine:
//   idle     → file picker + dropzone
//   parsing  → spinner + filenames being parsed
//   parsed   → ImportPreviewPanel rendered with the parsed data
//   error    → error block + "Try again" button → idle
//   committing → covered by ImportPreviewPanel's isCommitting prop
//
// Commit flow:
//   1. Operator picks files → parseXml posts multipart → parsed
//   2. Operator reviews/edits in ImportPreviewPanel
//   3. Click "Import N Sequences" → commit() → toast + navigate
//
// All error rendering uses parseApiErrorBody / formatApiError (the
// shared error-formatting pattern from Phases 5.10h + E-polish-extract).

import { useRef, useState, type ChangeEvent, type DragEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { importApi } from '@/lib/api/import';
import { useUiToast } from '@/hooks/useUiToast';
import { PageToast } from '@/components/shared/PageToast';
import {
  ImportPreviewPanel,
  type ImportParsedData,
} from '@/components/import/ImportPreviewPanel';

type TabState =
  | { kind: 'idle' }
  | { kind: 'parsing'; fileNames: string[] }
  | { kind: 'parsed'; data: ImportParsedData; fileNames: string[] }
  | { kind: 'error'; message: string; detail?: string };

const PARSE_ERROR_MESSAGES: Record<string, string> = {
  NoFilesUploaded: 'No XML files were uploaded.',
  InvalidFileType: 'All files must be .xml.',
  TooManyFiles: 'Too many files — maximum 20 per request.',
  FileTooLarge: 'A file is too large (10 MB per file limit).',
  ParseError: 'The XML file could not be parsed.',
  UnsupportedFormat: 'The XML is not a recognized eCTD format.',
  MissingRequiredXmlElement: 'The XML is missing required eCTD elements.',
  AuthRequired: 'Your session expired. Please sign in again.',
  PermissionDenied: 'You do not have permission to import.',
};

const COMMIT_ERROR_MESSAGES: Record<string, string> = {
  MissingRequiredField: 'A required field is missing.',
  EmptySequences: 'No sequences to import.',
  InvalidSequence: 'A sequence is missing required fields.',
  InvalidSummary: 'Import summary is invalid.',
  AuthRequired: 'Your session expired. Please sign in again.',
  PermissionDenied: 'You do not have permission to import.',
};

export function XmlTab(): ReactElement {
  const [state, setState] = useState<TabState>({ kind: 'idle' });
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const fileInputRef = useRef<globalThis.HTMLInputElement | null>(null);
  const { toast, showToast, clearToast } = useUiToast();
  const navigate = useNavigate();

  const triggerPicker = (): void => {
    fileInputRef.current?.click();
  };

  const handleFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    setState({ kind: 'parsing', fileNames: files.map((f) => f.name) });
    try {
      const data = await importApi.parseXml(files);
      setState({ kind: 'parsed', data, fileNames: files.map((f) => f.name) });
    } catch (err) {
      const message = formatApiError(err, 'Failed to parse XML.', PARSE_ERROR_MESSAGES);
      let detail: string | undefined;
      if (err instanceof ApiError) {
        try {
          const body = JSON.parse(err.body) as { details?: { detail?: string; fileName?: string } };
          if (body.details?.detail) detail = body.details.detail;
          else if (body.details?.fileName) detail = `File: ${body.details.fileName}`;
        } catch {
          /* ignore */
        }
      }
      setState(
        detail !== undefined
          ? { kind: 'error', message, detail }
          : { kind: 'error', message },
      );
    }
  };

  const onPickerChange = (e: ChangeEvent<globalThis.HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    void handleFiles(files);
  };

  const onDrop = (e: DragEvent<globalThis.HTMLDivElement>): void => {
    e.preventDefault();
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    void handleFiles(files);
  };

  const onDragOver = (e: DragEvent<globalThis.HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handlePreviewChange = (next: ImportParsedData): void => {
    setState((prev) => (prev.kind === 'parsed' ? { ...prev, data: next } : prev));
  };

  const handleCommit = async (data: ImportParsedData): Promise<void> => {
    setCommitting(true);
    setCommitError(null);
    try {
      const result = await importApi.commit(data);
      showToast({
        type: 'success',
        text: `Imported ${result.submissionIds.length} sequence${
          result.submissionIds.length === 1 ? '' : 's'
        } as draft metadata. Document files were not imported.`,
      });
      // Navigate to the first new submission's detail page — matches V1's
      // post-commit behaviour. Falls back to /home if no IDs returned.
      if (result.submissionIds.length > 0) {
        navigate(`/submissions/${result.submissionIds[0]!}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setCommitError(formatApiError(err, 'Failed to import sequences.', COMMIT_ERROR_MESSAGES));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div data-testid="xml-tab">
      {state.kind === 'idle' && (
        <div
          data-testid="xml-dropzone"
          onDrop={onDrop}
          onDragOver={onDragOver}
          style={{
            background: '#F9FAFB',
            border: '2px dashed #D1D5DB',
            borderRadius: 8,
            padding: '32px 16px',
            textAlign: 'center',
            color: '#6B7280',
            cursor: 'pointer',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }} aria-hidden="true">
            📄
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Drop XML files here or click to select
          </div>
          <div style={{ fontSize: 12 }}>
            index.xml, regional XML — up to 20 files, 10 MB each.
          </div>
          <button type="button" onClick={triggerPicker}>Choose XML files</button>
          <input
            ref={fileInputRef}
            data-testid="xml-file-picker"
            type="file"
            accept=".xml"
            multiple
            onChange={onPickerChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {state.kind === 'parsing' && (
        <div
          data-testid="xml-parsing"
          style={{
            padding: '24px 16px',
            textAlign: 'center',
            fontSize: 13,
            color: '#6B7280',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }} aria-hidden="true">
            ⏳
          </div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Parsing {state.fileNames.length} file
            {state.fileNames.length === 1 ? '' : 's'}…
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12 }}>
            {state.fileNames.map((n) => (
              <li key={n} data-testid="xml-parsing-filename">
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}

      {state.kind === 'parsed' && (
        <>
          {commitError && (
            <div
              data-testid="xml-commit-error"
              role="alert"
              style={{
                marginBottom: 12,
                padding: '8px 12px',
                background: '#FEE2E2',
                color: '#B91C1C',
                border: '1px solid #B91C1C',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {commitError}
            </div>
          )}
          <ImportPreviewPanel
            parsedData={state.data}
            isCommitting={committing}
            onChange={handlePreviewChange}
            onCommit={(data) => void handleCommit(data)}
            onCancel={() => {
              setState({ kind: 'idle' });
              setCommitError(null);
            }}
          />
        </>
      )}

      {state.kind === 'error' && (
        <div
          data-testid="xml-parse-error"
          role="alert"
          style={{
            padding: 16,
            background: '#FEE2E2',
            color: '#B91C1C',
            border: '1px solid #B91C1C',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{state.message}</div>
          {state.detail && (
            <div data-testid="xml-parse-error-detail" style={{ fontSize: 12, opacity: 0.85 }}>
              {state.detail}
            </div>
          )}
          <button
            type="button"
            data-testid="xml-try-again-btn"
            onClick={() => setState({ kind: 'idle' })}
            style={{
              marginTop: 12,
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              color: '#B91C1C',
              border: '1px solid #B91C1C',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}

      <PageToast toast={toast} onDismiss={clearToast} />
    </div>
  );
}
